// Connectivity check with actual API reachability test
// Default to ONLINE to avoid false offline detection

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const CHECK_TIMEOUT = 3000; // 3 seconds (reduced from 5)
const DEBOUNCE_MS = 3000; // 3 second debounce for offline detection

let lastCheckResult: boolean | null = null;
let lastCheckTime = 0;
const CACHE_DURATION = 5000; // Cache result for 5 seconds
let consecutiveFailures = 0;
const FAILURES_REQUIRED_FOR_OFFLINE = 2; // Require 2 consecutive failures

/**
 * Check actual connectivity by making a request to Supabase
 * IMPORTANT: Default to ONLINE - only mark offline after multiple failures
 */
export const checkConnectivity = async (): Promise<boolean> => {
  // Quick check - if browser explicitly says offline, trust it
  if (!navigator.onLine) {
    lastCheckResult = false;
    lastCheckTime = Date.now();
    consecutiveFailures = FAILURES_REQUIRED_FOR_OFFLINE; // Immediate offline
    return false;
  }

  // Use cached result if recent enough
  const now = Date.now();
  if (lastCheckResult !== null && (now - lastCheckTime) < CACHE_DURATION) {
    return lastCheckResult;
  }

  // If Supabase URL is not configured, default to online
  if (!SUPABASE_URL) {
    console.log('[ConnectivityCheck] No Supabase URL configured, defaulting to online');
    lastCheckResult = true;
    lastCheckTime = Date.now();
    return true;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT);

    // Simple HEAD request to Supabase REST API
    const response = await fetch(`${SUPABASE_URL}/rest/v1/`, {
      method: 'HEAD',
      signal: controller.signal,
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '',
      },
    });

    clearTimeout(timeoutId);
    
    // Any response (even 4xx) means we have connectivity
    lastCheckResult = true;
    lastCheckTime = Date.now();
    consecutiveFailures = 0; // Reset failures on success
    return true;
  } catch (error) {
    consecutiveFailures++;
    console.log('[ConnectivityCheck] Connectivity check failed (attempt', consecutiveFailures, '):', error);
    
    // Only mark as offline after multiple consecutive failures
    if (consecutiveFailures >= FAILURES_REQUIRED_FOR_OFFLINE) {
      lastCheckResult = false;
      lastCheckTime = Date.now();
      return false;
    }
    
    // Not enough failures yet - default to online
    console.log('[ConnectivityCheck] Not enough failures yet, defaulting to online');
    return true;
  }
};

/**
 * Create a debounced offline handler to prevent flickering
 */
export const createDebouncedOfflineHandler = (
  onOffline: () => void,
  onOnline: () => void
): { handleOnline: () => void; handleOffline: () => void; cleanup: () => void } => {
  let pendingOfflineTimeout: ReturnType<typeof setTimeout> | null = null;

  const handleOnline = async () => {
    // Clear any pending offline check immediately
    if (pendingOfflineTimeout) {
      clearTimeout(pendingOfflineTimeout);
      pendingOfflineTimeout = null;
    }

    // Verify actual connectivity before declaring online
    const isConnected = await checkConnectivity();
    if (isConnected) {
      console.log('[ConnectivityCheck] Confirmed online');
      onOnline();
    }
  };

  const handleOffline = () => {
    // Debounce offline detection to prevent flicker on flaky connections
    if (pendingOfflineTimeout) {
      clearTimeout(pendingOfflineTimeout);
    }

    pendingOfflineTimeout = setTimeout(async () => {
      const isConnected = await checkConnectivity();
      if (!isConnected) {
        console.log('[ConnectivityCheck] Confirmed offline after debounce');
        onOffline();
      }
      pendingOfflineTimeout = null;
    }, DEBOUNCE_MS);
  };

  const cleanup = () => {
    if (pendingOfflineTimeout) {
      clearTimeout(pendingOfflineTimeout);
      pendingOfflineTimeout = null;
    }
  };

  return { handleOnline, handleOffline, cleanup };
};

/**
 * Clear cached connectivity result
 */
export const clearConnectivityCache = (): void => {
  lastCheckResult = null;
  lastCheckTime = 0;
};
